import type { Location } from "@skyware-group/iris-api-model";

import { BaseClient } from "../../clients/base/BaseClient";
import type { Paged } from "../_shared/pagination.types";
import { LOCATIONS_ENDPOINTS } from "./locations.consts";
import type { LocationCreateRequest, LocationUpdateRequest, LocationsQueryParams } from "./locations.types";

/**
 * Locations API Client
 * Handles all location CRUD operations. Admin-only endpoints.
 */
export class LocationsClient extends BaseClient {
  /**
   * Fetch a page of locations.
   * @param params - Optional pagination parameters.
   * @param signal - Optional abort signal.
   */
  async fetchLocations(params?: LocationsQueryParams, signal?: AbortSignal | null): Promise<Paged<Location>> {
    return this.get<Paged<Location>>(LOCATIONS_ENDPOINTS.LOCATIONS, {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  /**
   * Fetch a single location by ID. Response includes instruments[].
   * @param id - Location ID.
   * @param signal - Optional abort signal.
   */
  async fetchLocationById(id: number, signal?: AbortSignal | null): Promise<Location> {
    return this.get<Location>(LOCATIONS_ENDPOINTS.LOCATION_BY_ID(id), { signal });
  }

  /**
   * Create a new location.
   * @param data - Location create request.
   * @param signal - Optional abort signal.
   */
  async createLocation(data: LocationCreateRequest, signal?: AbortSignal | null): Promise<Location> {
    return this.post<Location>(LOCATIONS_ENDPOINTS.CREATE_LOCATION, { body: data, signal });
  }

  /**
   * Update an existing location.
   * @param id - Location ID.
   * @param data - Location update request.
   * @param signal - Optional abort signal.
   */
  async updateLocation(id: number, data: LocationUpdateRequest, signal?: AbortSignal | null): Promise<Location> {
    return this.put<Location>(LOCATIONS_ENDPOINTS.UPDATE_LOCATION(id), { body: data, signal });
  }

  /**
   * Delete a location.
   * @param id - Location ID.
   * @param signal - Optional abort signal.
   */
  async deleteLocation(id: number, signal?: AbortSignal | null): Promise<void> {
    return this.delete<never>(LOCATIONS_ENDPOINTS.DELETE_LOCATION(id), { signal });
  }
}
